import React, { useState, useEffect, useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  X,
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Heart,
  Share2,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";

// Helper function to get media URL
const getMediaUrl = (uuid: string): string => {
  return `/api/public/media/${uuid}`;
};

// Helper function to get thumbnail URL
const getThumbnailUrl = (uuid: string): string => {
  return `/api/public/thumbnails/${uuid}`;
};

interface Media {
  id: number;
  user_id: number;
  uuid: string;
  url: string;
  mime_type: string;
  file_size?: number;
  original_filename?: string;
  title?: string;
  description?: string;
  tags?: string[];
  r_user?: string;
  r_subreddit?: string;
  source?: string;
  people?: string[];
  time_watched: number;
  view_count: number;
  thumbnail_url?: string;
  thumbnail_generated: boolean;
  created_at: string;
  updated_at: string;
}

interface MediaViewerProps {
  media: Media;
  isOpen: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  isLoadingNext?: boolean;
}

export function MediaViewer({
  media,
  isOpen,
  onClose,
  onNext,
  onPrevious,
  isLoadingNext = false,
}: MediaViewerProps) {
  const { token } = useAuth();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [viewStartTime, setViewStartTime] = useState<number | null>(null);
  const [totalViewTime, setTotalViewTime] = useState(0);
  const [showControls, setShowControls] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const viewTrackingRef = useRef<NodeJS.Timeout | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const showControlsTemporarily = () => {
    setShowControls(true);

    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Hide controls after 3 seconds on mobile
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  useEffect(() => {
    if (isOpen) {
      setViewStartTime(Date.now());
      setTotalViewTime(0);

      // Start view tracking
      viewTrackingRef.current = setInterval(() => {
        setTotalViewTime((prev) => prev + 1);
      }, 1000);

      // Show controls briefly on mobile when opening video
      if (media.mime_type.startsWith("video/") && "ontouchstart" in window) {
        showControlsTemporarily();
      }

      // Ensure GIFs auto-play when opening
      if (media.mime_type === "image/gif" && videoRef.current) {
        setTimeout(() => {
          if (videoRef.current) {
            videoRef.current.play().catch(console.error);
          }
        }, 100);
      }
    } else {
      // Send view tracking data when closing
      if (viewStartTime && totalViewTime > 0) {
        trackView(totalViewTime);
      }

      // Cleanup
      if (viewTrackingRef.current) {
        clearInterval(viewTrackingRef.current);
        viewTrackingRef.current = null;
      }
      setViewStartTime(null);
      setTotalViewTime(0);
      setShowControls(false);
    }

    return () => {
      if (viewTrackingRef.current) {
        clearInterval(viewTrackingRef.current);
      }
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isOpen, media.id]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case "Escape":
          onClose();
          break;
        case " ":
          e.preventDefault();
          togglePlayPause();
          break;
        case "ArrowLeft":
          onPrevious?.();
          break;
        case "ArrowRight":
          onNext?.();
          break;
        case "m":
          toggleMute();
          break;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isPlaying]);

  const trackView = async (duration: number) => {
    try {
      await fetch(`/api/media/${media.id}/view`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ duration }),
      });
    } catch (err) {
      console.error("Failed to track view:", err);
    }
  };

  const togglePlayPause = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);

    // Show controls briefly when toggling play/pause on mobile
    showControlsTemporarily();
  };

  const handleVideoClick = () => {
    // For GIFs, toggle play/pause directly
    if (media.mime_type === "image/gif") {
      togglePlayPause();
      showControlsTemporarily();
      return;
    }

    // On mobile/touch devices, show controls instead of immediate play/pause
    if ("ontouchstart" in window) {
      showControlsTemporarily();
    } else {
      togglePlayPause();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;

    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
  };

  const handleSeek = (
    e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>
  ) => {
    if (!videoRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    let clientX: number;

    if ("touches" in e) {
      // Touch event
      clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
    } else {
      // Mouse event
      clientX = e.clientX;
    }

    const percent = (clientX - rect.left) / rect.width;
    const newTime = Math.max(0, Math.min(duration, percent * duration));

    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    // Keep controls visible when seeking
    showControlsTemporarily();
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const getMediaUrl = (uuid: string) => {
    // Use public UUID-based endpoint (no auth required)
    return `/api/public/media/${uuid}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/50 to-transparent p-4">
        <div className="flex items-center justify-between text-white">
          <div className="flex-1 max-w-[75vw]">
            <h2 className="text-lg font-semibold truncate">
              {media.title || media.original_filename || "Untitled"}
            </h2>
            <p className="text-sm opacity-80">
              {media.view_count} views •{" "}
              {new Date(media.created_at).toLocaleDateString()}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Media Content */}
      <div className="relative w-full h-full flex items-center justify-center">
        {media.mime_type.startsWith("image/") &&
        media.mime_type !== "image/gif" ? (
          <img
            src={getMediaUrl(media.uuid)}
            alt={media.title || media.original_filename || "Media"}
            className="max-w-full max-h-full object-contain"
            onClick={onClose}
          />
        ) : media.mime_type.startsWith("video/") ||
          media.mime_type === "image/gif" ? (
          <div className="relative group w-full h-full flex items-center justify-center">
            <video
              ref={videoRef}
              src={getMediaUrl(media.uuid)}
              poster={
                media.thumbnail_generated
                  ? getThumbnailUrl(media.uuid)
                  : undefined
              }
              className="max-w-full max-h-full object-contain"
              onClick={handleVideoClick}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              autoPlay
              muted
              loop
              playsInline
            />

            {/* Video Controls Overlay - Responsive visibility */}
            <div
              className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transition-opacity ${
                showControls
                  ? "opacity-100"
                  : "opacity-0 md:group-hover:opacity-100"
              }`}
            >
              {/* Progress Bar - Functional for both videos and GIFs */}
              <div
                className="w-full h-3 md:h-2 bg-white/30 rounded cursor-pointer mb-3 touch-manipulation"
                onClick={handleSeek}
                onTouchEnd={handleSeek}
              >
                <div
                  className="h-full bg-white rounded"
                  style={{ width: `${(currentTime / duration) * 100}%` }}
                />
              </div>

              {/* Controls */}
              <div className="flex items-center justify-between text-white">
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={togglePlayPause}
                  >
                    {isPlaying ? (
                      <Pause className="h-5 w-5" />
                    ) : (
                      <Play className="h-5 w-5" />
                    )}
                  </Button>

                  {/* Mute button only for videos */}
                  {media.mime_type.startsWith("video/") && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                      onClick={toggleMute}
                    >
                      {isMuted ? (
                        <VolumeX className="h-5 w-5" />
                      ) : (
                        <Volume2 className="h-5 w-5" />
                      )}
                    </Button>
                  )}

                  {/* Time display for videos and GIFs */}
                  <span className="text-sm flex items-center gap-2">
                    {media.mime_type === "image/gif" && (
                      <span className="bg-white/20 px-2 py-1 rounded text-xs">
                        GIF
                      </span>
                    )}
                    <span>
                      {formatTime(currentTime)} / {formatTime(duration)}
                    </span>
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  {onPrevious && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20"
                      onClick={onPrevious}
                    >
                      Previous
                    </Button>
                  )}
                  {onNext && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20"
                      onClick={onNext}
                      disabled={isLoadingNext}
                    >
                      {isLoadingNext ? (
                        <div className="flex items-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Loading...</span>
                        </div>
                      ) : (
                        "Next"
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-white">
            <p className="text-lg mb-2">File type not supported for preview</p>
            <p className="text-sm opacity-80">{media.mime_type}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => window.open(getMediaUrl(media.uuid), "_blank")}
            >
              Download
            </Button>
          </div>
        )}
      </div>

      {/* Side Info Panel (responsive) */}
      <div className="hidden md:block absolute right-0 top-0 bottom-0 w-80 bg-black/80 backdrop-blur-sm overflow-y-auto">
        <div className="p-6 text-white">
          <h3 className="text-lg font-semibold mb-4">Details</h3>

          {media.description && (
            <div className="mb-4">
              <h4 className="text-sm font-medium opacity-80 mb-1">
                Description
              </h4>
              <p className="text-sm">{media.description}</p>
            </div>
          )}

          {media.tags && media.tags.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium opacity-80 mb-2">Tags</h4>
              <div className="flex flex-wrap gap-1">
                {media.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-1 bg-white/20 text-xs rounded"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {media.people && media.people.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium opacity-80 mb-2">People</h4>
              <div className="flex flex-wrap gap-1">
                {media.people.map((person) => (
                  <span
                    key={person}
                    className="px-2 py-1 bg-white/20 text-xs rounded"
                  >
                    {person}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="opacity-80">Views:</span>
              <span>{media.view_count}</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">Watch time:</span>
              <span>{Math.round(media.time_watched / 60)} min</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">File size:</span>
              <span>{(media.file_size / (1024 * 1024)).toFixed(1)} MB</span>
            </div>
            <div className="flex justify-between">
              <span className="opacity-80">Created:</span>
              <span>{new Date(media.created_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-white/20">
            <p className="text-xs opacity-60">
              Current session: {totalViewTime}s viewed
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
